import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import siteConfig, { formatPrice } from "../config/siteConfig";
import "../css/paysuccess.css";

const ORDER_STORAGE_KEY = "hushcider:last-order";

const readStoredOrder = () => {
  if (typeof window === "undefined") return null;

  try {
    const storedOrder = window.sessionStorage.getItem(ORDER_STORAGE_KEY);
    return storedOrder ? JSON.parse(storedOrder) : null;
  } catch {
    return null;
  }
};

const PaySuccess = () => {
  const location = useLocation();
  const [order] = useState(() => location.state?.order || readStoredOrder());

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (!order?.orderNumber) {
    return (
      <main className="pay-success pay-success--missing">
        <section className="pay-success__missing" aria-labelledby="missing-order-title">
          <p className="pay-success__eyebrow">{siteConfig.wordmark} / ORDERS</p>
          <h1 id="missing-order-title">NO RECENT ORDER.</h1>
          <p>
            This confirmation is available only in the browser session where
            checkout was completed.
          </p>
          <Link className="pay-success__button" to="/shop" onClick={scrollToTop}>
            RETURN TO SHOP
          </Link>
        </section>
      </main>
    );
  }

  const placedAt = new Intl.DateTimeFormat(siteConfig.commerce.locale, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(order.placedAt));

  return (
    <main className="pay-success">
      <section className="pay-success__hero" aria-labelledby="order-confirmed-title">
        <div className="pay-success__status" aria-hidden="true">
          <span />
        </div>
        <div className="pay-success__hero-copy">
          <p className="pay-success__eyebrow">ORDER RECEIVED / PAYMENT CONFIRMED</p>
          <h1 id="order-confirmed-title">YOU’RE ALL SET.</h1>
          <p>
            Thanks, {order.customerName}. A confirmation has been prepared for {" "}
            <strong>{order.contact}</strong>.
          </p>
        </div>
        <div className="pay-success__reference">
          <span>ORDER NUMBER</span>
          <strong>{order.orderNumber}</strong>
          <small>{placedAt}</small>
        </div>
      </section>

      <div className="pay-success__layout">
        <section className="pay-success__summary" aria-labelledby="success-summary-title">
          <div className="pay-success__section-title">
            <span>01</span>
            <h2 id="success-summary-title">ORDER SUMMARY</h2>
          </div>

          <div className="pay-success__items">
            {order.items?.map((item) => (
              <article className="pay-success-item" key={`${item.id}-${item.size}-${item.color}`}>
                <img src={item.image} alt={item.name} />
                <div>
                  <h3>{item.name}</h3>
                  <p>{[item.size, item.color].filter(Boolean).join(" / ")}</p>
                  <span>QTY {item.quantity}</span>
                </div>
                <strong>{formatPrice(item.lineTotal)}</strong>
              </article>
            ))}
          </div>

          <div className="pay-success__totals">
            <div>
              <span>SUBTOTAL</span>
              <span>{formatPrice(order.subtotal)}</span>
            </div>
            {order.discount > 0 && (
              <div className="is-discount">
                <span>PROMOTION ({order.discountCode})</span>
                <span>−{formatPrice(order.discount)}</span>
              </div>
            )}
            <div>
              <span>SHIPPING</span>
              <span>{order.shipping === 0 ? "FREE" : formatPrice(order.shipping)}</span>
            </div>
            <div className="pay-success__total">
              <span>TOTAL</span>
              <strong>{formatPrice(order.total)}</strong>
            </div>
          </div>
        </section>

        <aside className="pay-success__details" aria-labelledby="success-next-title">
          <div className="pay-success__section-title">
            <span>02</span>
            <h2 id="success-next-title">WHAT’S NEXT</h2>
          </div>

          <ol className="pay-success__timeline">
            <li className="is-active">
              <span>01</span>
              <div>
                <strong>ORDER CONFIRMED</strong>
                <p>Your paid order has been recorded for this session.</p>
              </div>
            </li>
            <li>
              <span>02</span>
              <div>
                <strong>PACKING</strong>
                <p>The fulfillment team will prepare your gear next.</p>
              </div>
            </li>
            <li>
              <span>03</span>
              <div>
                <strong>TRACKING</strong>
                <p>Tracking will be sent to {order.contact} after dispatch.</p>
              </div>
            </li>
          </ol>

          <dl className="pay-success__delivery">
            <div>
              <dt>SHIP TO</dt>
              <dd>{order.destination}</dd>
            </div>
            <div>
              <dt>METHOD</dt>
              <dd>{order.shippingMethod}</dd>
            </div>
            <div>
              <dt>ESTIMATE</dt>
              <dd>{order.deliveryEstimate}</dd>
            </div>
          </dl>

          <p className="pay-success__demo-note">PAYMENT DETAILS WERE HANDLED BY THE SECURE PAYMENT PROVIDER.</p>

          <div className="pay-success__actions">
            <Link className="pay-success__button" to="/shop" onClick={scrollToTop}>
              RETURN TO SHOP <span aria-hidden="true">→</span>
            </Link>
            <Link className="pay-success__text-link" to="/contact" onClick={scrollToTop}>
              NEED HELP? CONTACT US
            </Link>
          </div>
        </aside>
      </div>
    </main>
  );
};

export default PaySuccess;
